import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import loginImage from "../assets/Login.webp";

// const BASE_URL = "http://localhost:9091/api/auth";
const BASE_URL = "https://api.mediitacademy.com/api/auth";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.194 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 16.108 19.001 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.144 35.091 26.715 36 24 36c-5.173 0-9.625-3.329-11.287-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.084 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isIdentifierValid = emailOrMobile.trim().length >= 5;

  const getRedirectPath = () => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");

    if (redirect && redirect.startsWith("/")) {
      if (redirect === "/login" || redirect === "/register") {
        return "/student-dashboard";
      }

      return redirect;
    }

    return "/student-dashboard";
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      navigate(getRedirectPath(), { replace: true });
    }
  }, [navigate]);

  const saveLoginData = (data: any) => {
    const studentId =
      data.studentId ||
      data.student_id ||
      data.userId ||
      data.user_id ||
      data.id ||
      "";

    const studentName =
      data.studentName ||
      data.fullName ||
      data.full_name ||
      data.name ||
      "Student";

    const email = data.email || "";
    const mobile = data.mobile || data.mobile_number || data.phone || "";

    const studentObj = {
      id: studentId,
      studentId,
      fullName: studentName,
      name: studentName,
      email,
      mobile,
      phone: mobile,
      course: data.course || "",
      studentClass: data.school || data.studentClass || data.class || "",
    };

    if (data.token) {
      localStorage.setItem("token", data.token);
    }

    localStorage.setItem("student", JSON.stringify(studentObj));
    localStorage.setItem("user", JSON.stringify(studentObj));

    if (studentId) {
      localStorage.setItem("studentId", String(studentId));
      localStorage.setItem("userId", String(studentId));
      localStorage.setItem("id", String(studentId));
    }

    if (studentName) {
      localStorage.setItem("studentName", studentName);
      localStorage.setItem("name", studentName);
    }

    if (email) {
      localStorage.setItem("email", email);
    }

    if (mobile) {
      localStorage.setItem("mobile", mobile);
      localStorage.setItem("phone", mobile);
    }
  };

  const handleSendOtp = async () => {
    if (!isIdentifierValid) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const response = await fetch(`${BASE_URL}/login-send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrMobile: emailOrMobile.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
      } else {
        setErrorMsg(data.message || "Account not found in our database.");
      }
    } catch (error) {
      setErrorMsg("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      const response = await fetch(`${BASE_URL}/login-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrMobile: emailOrMobile.trim(), otp }),
      });

      const data = await response.json();

      if (response.ok) {
        saveLoginData(data);

        navigate(getRedirectPath(), { replace: true });
      } else {
        setErrorMsg(data.message || "Invalid or Expired OTP.");
      }
    } catch (error) {
      setErrorMsg("Verification server error.");
    } finally {
      setLoading(false);
    }
  };

  const resetLogin = () => {
    setOtp("");
    setOtpSent(false);
    setErrorMsg(null);
  };

  return (
    <div
      className="h-screen overflow-hidden bg-cover bg-center bg-no-repeat text-[#101827] font-['Inter']"
      style={{
        backgroundImage:
          "url('https://img.freepik.com/premium-vector/modern-simple-blue-gold-abstract-background-suitable-business-card-presentation-flyer-brochure-print-certificate-template_181182-16594.jpg')",
      }}
    >
      <div className="mx-auto grid h-screen max-w-[1280px] grid-cols-1 lg:grid-cols-2 lg:gap-8">
        <div className="relative hidden h-screen items-center justify-center overflow-hidden px-6 lg:flex">
          <div className="absolute left-16 top-20 h-28 w-28 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute bottom-16 right-20 h-36 w-36 rounded-full bg-orange-100 blur-3xl" />

          <div className="relative flex max-w-[540px] flex-col items-center text-center">
            <div className="mb-5 text-center">
              <h2 className="max-w-xl text-3xl font-black leading-tight tracking-[-0.04em] text-slate-950 xl:text-4xl">
                Welcome back to Mediit Academy
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-6 text-slate-600">
                Login securely to continue learning from where you left off.
              </p>
            </div>

            <div className="relative h-[360px] w-[410px]">
              <div className="absolute inset-x-8 bottom-2 h-[290px] rounded-full border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.12)]" />

              <div className="absolute left-1/2 top-[-20px] h-[390px] w-[390px] -translate-x-1/2 overflow-visible">
                <img
                  src={loginImage}
                  alt="Student login"
                  className="h-full w-full scale-[1.16] object-contain object-center"
                />
              </div>

              <div className="absolute left-4 top-20 rounded-2xl bg-white px-4 py-3 text-left shadow-xl">
                <p className="text-sm font-black text-slate-950">
                  Verified Access
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Login with secure OTP
                </p>
              </div>

              <div className="absolute bottom-8 left-1/2 flex w-[260px] -translate-x-1/2 items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/95 px-4 py-3 text-left shadow-xl backdrop-blur-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                  🔐
                </div>

                <div>
                  <p className="text-sm font-black text-slate-950">
                    Secure OTP Login
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Email or mobile verification
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-screen items-center justify-center px-4 py-4 sm:px-6">
          <div className="w-full max-w-[420px] rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            <div className="mb-6 text-center lg:text-left">
              <a
                href="/"
                className="mb-4 inline-flex items-center text-xs font-bold text-blue-600 hover:underline"
              >
                ← Back to home
              </a>

              <h1 className="text-3xl font-black tracking-[-0.04em] text-slate-950 xl:text-4xl">
                Continue your learning journey
              </h1>

              <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
                Login with your email or mobile number and access your
                dashboard.
              </p>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="text-xs font-black text-slate-800">
                  Email or Mobile Number
                </label>

                <div className="mt-1.5 flex gap-2">
                  <input
                    type="text"
                    disabled={otpSent}
                    value={emailOrMobile}
                    onChange={(e) => {
                      setEmailOrMobile(e.target.value);
                      setErrorMsg(null);
                    }}
                    placeholder="Enter registered email or mobile"
                    className="h-11 flex-1 rounded-lg border border-slate-300 px-3 text-[13px] outline-none transition-all focus:border-blue-600 focus:ring-3 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-500"
                    required
                  />

                  {!otpSent && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={!isIdentifierValid || loading}
                      className={`h-11 shrink-0 rounded-lg border px-4 text-xs font-black transition-all ${
                        isIdentifierValid && !loading
                          ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                          : "cursor-not-allowed border-slate-300 text-slate-400"
                      }`}
                    >
                      {loading ? "Sending..." : "Send OTP"}
                    </button>
                  )}
                </div>

                {otpSent && (
                  <button
                    type="button"
                    onClick={resetLogin}
                    className="mt-2 text-[11px] font-bold text-slate-500 hover:text-blue-600 hover:underline"
                  >
                    Change email/mobile
                  </button>
                )}
              </div>

              {otpSent && (
                <div>
                  <label className="text-xs font-black text-slate-800">
                    Enter OTP
                  </label>

                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="Enter 6-digit OTP"
                    className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-[13px] tracking-[0.2em] outline-none transition-all focus:border-blue-600 focus:ring-3 focus:ring-blue-50"
                    required
                  />

                  <div className="mt-1.5 text-right">
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={loading}
                      className="text-[11px] font-bold text-blue-600 hover:underline disabled:text-slate-400"
                    >
                      {loading ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>
                </div>
              )}

              {errorMsg && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-[11px] font-bold text-red-600">
                  ⚠️ {errorMsg}
                </div>
              )}

              {otpSent && (
                <button
                  type="submit"
                  disabled={otp.length < 4 || loading}
                  className={`h-11 w-full rounded-lg text-xs font-black shadow-lg transition-all ${
                    otp.length >= 4 && !loading
                      ? "bg-[#101827] text-white hover:-translate-y-0.5 hover:bg-slate-800"
                      : "cursor-not-allowed bg-slate-300 text-slate-500 shadow-none"
                  }`}
                >
                  {loading ? "Verifying..." : "Secure Login"}
                </button>
              )}
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[11px] font-medium text-slate-400">
                or continue with
              </span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <button
              type="button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-800 transition-all hover:border-slate-400 hover:bg-slate-50"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <p className="mt-4 text-center text-xs text-slate-600">
              New to Mediit Academy?{" "}
              <a
                href="/register"
                className="font-black text-blue-600 hover:underline"
              >
                Create account
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;